import React from "react";
import { gsap } from "gsap";
import { type Shape } from "~/features/ui/slider";
import { animateCardIn, animateCardOut, AUTOPLAY_DURATION } from "./shared";
export function usePartnerGallery(itemCount: number) {
  const sliderRef = React.useRef<Shape>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [sliderIndex, setSliderIndex] = React.useState(0);
  const [lastSlideIndex, setLastSlideIndex] = React.useState(0);
  const cardContentRef = React.useRef<HTMLDivElement>(null);
  const mediaContainerRef = React.useRef<HTMLDivElement>(null);
  const timelineRef = React.useRef<gsap.core.Timeline | null>(null);
  const zoomTweenRef = React.useRef<gsap.core.Tween | null>(null);
  const activeIndexRef = React.useRef(activeIndex);
  activeIndexRef.current = activeIndex;
  const animVersionRef = React.useRef(0);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const SplitTextRef = React.useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const splitInstancesRef = React.useRef<any[]>([]);
  React.useEffect(() => {
    if (typeof window !== "undefined" && !SplitTextRef.current) {
      import("gsap/SplitText").then(module => {
        SplitTextRef.current = module.SplitText;
        gsap.registerPlugin(module.SplitText);
      });
    }
  }, []);
  const changeSlide = React.useCallback((newIndex: number) => {
    if (newIndex === activeIndexRef.current) return;
    const version = ++animVersionRef.current;
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }
    const card = cardContentRef.current;
    if (!card) {
      setActiveIndex(newIndex);
      return;
    }
    const exitTl = animateCardOut(card);
    timelineRef.current = exitTl;
    exitTl.eventCallback("onComplete", () => {
      if (animVersionRef.current !== version) return;
      splitInstancesRef.current.forEach(s => s.revert());
      splitInstancesRef.current = [];
      setActiveIndex(newIndex);
      requestAnimationFrame(() => {
        const allElements = card.querySelectorAll("[data-animate]");
        gsap.set(allElements, {
          opacity: 0
        });
        requestAnimationFrame(() => {
          if (animVersionRef.current !== version) return;
          const {
            tl: enterTl,
            splits
          } = animateCardIn(card, SplitTextRef.current);
          timelineRef.current = enterTl;
          splitInstancesRef.current = splits;
        });
      });
    });
  }, []);
  const handleAutoAdvance = React.useCallback(() => {
    const next = (activeIndexRef.current + 1) % (itemCount || 1);
    changeSlide(next);
  }, [itemCount, changeSlide]);
  const handleSliderMounted = React.useCallback((splide: {
    Components: {
      Controller: {
        getEnd: () => number;
      };
    };
  }) => {
    setLastSlideIndex(splide.Components.Controller.getEnd());
  }, []);
  const handleSliderMove = React.useCallback((splide: {
    Components: {
      Controller: {
        getIndex: () => number;
        getEnd: () => number;
      };
    };
  }) => {
    setSliderIndex(splide.Components.Controller.getIndex());
    setLastSlideIndex(splide.Components.Controller.getEnd());
  }, []);
  const handleSliderResized = React.useCallback((splide: {
    Components: {
      Controller: {
        getEnd: () => number;
      };
    };
  }) => {
    setLastSlideIndex(splide.Components.Controller.getEnd());
  }, []);
  React.useEffect(() => {
    const card = cardContentRef.current;
    if (!card) return;
    const {
      splits
    } = animateCardIn(card, SplitTextRef.current);
    splitInstancesRef.current = splits;
  }, []);
  React.useEffect(() => {
    if (zoomTweenRef.current) {
      zoomTweenRef.current.kill();
      zoomTweenRef.current = null;
    }
    const container = mediaContainerRef.current;
    if (!container) return;
    const zoomEl = container.querySelector(`[data-media-idx="${activeIndex}"] [data-zoom]`);
    if (!zoomEl) return;
    zoomTweenRef.current = gsap.fromTo(zoomEl, {
      scale: 1.06
    }, {
      scale: 1,
      duration: AUTOPLAY_DURATION / 1000,
      ease: "power1.out"
    });
  }, [activeIndex]);
  React.useEffect(() => {
    return () => {
      if (timelineRef.current) {
        timelineRef.current.kill();
      }
      if (zoomTweenRef.current) {
        zoomTweenRef.current.kill();
      }
      splitInstancesRef.current.forEach(s => s.revert());
      splitInstancesRef.current = [];
    };
  }, []);
  return {
    activeIndex,
    sliderRef,
    sliderIndex,
    lastSlideIndex,
    cardContentRef,
    mediaContainerRef,
    changeSlide,
    handleAutoAdvance,
    handleSliderMounted,
    handleSliderMove,
    handleSliderResized
  };
}